import { ensureAn26DesignStyles } from "./injectStyles.js";
import type { AverageReturnsPayload } from "../normalize.js";
import type { ChartLayout } from "../types.js";
import { formatRichTextHtml } from "./richText.js";

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export interface AverageReturnsRenderOptions {
  title?: string | null;
  disclaimer?: string | null;
  layout?: ChartLayout;
}

type Variant = "net" | "pureGross";

export function mountAverageReturns(
  container: HTMLElement,
  data: AverageReturnsPayload,
  opts?: AverageReturnsRenderOptions
): () => void {
  ensureAn26DesignStyles();
  const embedded = opts?.layout === "embedded";
  const title = opts?.title ?? "Average Annual Total Returns";
  const disclaimer =
    opts?.disclaimer === null ? "" : (opts?.disclaimer ?? data.disclaimer ?? "");
  let variant: Variant = "pureGross";
  const uid = `an26-ar-${Math.random().toString(36).slice(2, 9)}`;

  const headerCells = data.periods
    .map((p) => {
      const sub = p.subLabel
        ? `<br/><span class="an26-avgret__th-sub">${esc(p.subLabel)}</span>`
        : "";
      return `<th>${esc(p.label)}${sub}</th>`;
    })
    .join("");

  const valueRow = data.periods
    .map((p) => {
      const v = variant === "net" ? p.net : p.pureGross;
      return `<td class="an26-avgret__td-val js-an26-ar-val">${v.toFixed(2)}%</td>`;
    })
    .join("");

  const head = embedded
    ? ""
    : `<header class="an26-root__head">
        <h2 class="an26-root__title">${esc(String(title))}</h2>
        <p class="an26-root__subtitle an26-root__subtitle--avg">As of ${esc(data.asOf)}</p>
      </header>`;
  const rootClass = embedded ? "an26-root an26-root--avg an26-root--embedded" : "an26-root an26-root--avg";

  container.innerHTML = `
    <div class="${rootClass}">
      ${head}
      <div class="an26-split an26-split--avg">
        <div class="an26-avgret__col">
          <div class="an26-avgret__toolbar">
            <div class="an26-toggle" role="group" aria-label="Return basis">
              <button type="button" class="an26-toggle__btn js-an26-ar-gross is-active" id="${uid}-gross" aria-pressed="true">Pure Gross</button>
              <button type="button" class="an26-toggle__btn js-an26-ar-net" id="${uid}-net" aria-pressed="false">Net</button>
            </div>
          </div>
          <div class="an26-avgret__tablewrap">
            <table class="an26-table an26-avgret__table" role="grid">
              <thead><tr><th class="an26-avgret__th-corner" aria-hidden="true"></th>${headerCells}</tr></thead>
              <tbody>
                <tr><td class="an26-avgret__td-ticker">${esc(data.ticker || "—")}</td>${valueRow}</tr>
              </tbody>
            </table>
          </div>
        </div>
        ${disclaimer ? `<aside class="an26-avgret__aside"><p class="an26-avgret__disc">${formatRichTextHtml(disclaimer)}</p></aside>` : ""}
      </div>
    </div>`;

  const applyVariant = (v: Variant) => {
    variant = v;
    const cells = container.querySelectorAll(".js-an26-ar-val");
    cells.forEach((cell, i) => {
      const p = data.periods[i];
      if (!p) return;
      const num = v === "net" ? p.net : p.pureGross;
      cell.textContent = `${num.toFixed(2)}%`;
    });
    container.querySelector(".js-an26-ar-net")?.classList.toggle("is-active", v === "net");
    container.querySelector(".js-an26-ar-gross")?.classList.toggle("is-active", v === "pureGross");
    container.querySelector(".js-an26-ar-net")?.setAttribute("aria-pressed", v === "net" ? "true" : "false");
    container.querySelector(".js-an26-ar-gross")?.setAttribute("aria-pressed", v === "pureGross" ? "true" : "false");
  };

  const onNet = () => applyVariant("net");
  const onGross = () => applyVariant("pureGross");
  const netEl = container.querySelector(".js-an26-ar-net");
  const grossEl = container.querySelector(".js-an26-ar-gross");
  netEl?.addEventListener("click", onNet);
  grossEl?.addEventListener("click", onGross);

  return () => {
    netEl?.removeEventListener("click", onNet);
    grossEl?.removeEventListener("click", onGross);
    container.replaceChildren();
  };
}
